import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import SmartImage, { IMG } from "@/components/SmartImage";
import { HomeReels } from "@/components/Reels";

export default function Home() {
  const [event, setEvent] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvent(r.data[0] || null)).catch(() => {});
    api.get("/athletes", { params: { featured: true } }).then((r) => setAthletes(r.data.slice(0, 8))).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-black grain min-h-[92vh] flex items-center" data-testid="home-hero">
        <div className="absolute inset-0 pointer-events-none">
          <img src="/hero-arena.webp" alt="Ironveil Era arena" className="absolute inset-0 h-full w-full object-cover object-[70%_center] lg:object-[center_30%] opacity-80" data-testid="hero-bg-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20 lg:via-black/55 lg:to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pt-24 lg:pt-0 pb-16 w-full">
          <div className="max-w-[720px]">
            <div className="type-label text-[#c9c9c9] mb-6 reveal-up flex items-center gap-3"><span className="w-6 h-[2px] bg-[#C8102E]" />IVE / Ironveil Era</div>
            <h1 className="type-h1 reveal-up delay-1" data-testid="hero-title">
              Built for Combat.<br/>Built for <span className="text-[#C8102E]">Legacy.</span>
            </h1>
            <p className="mt-8 text-[#d0d0d0] text-base sm:text-lg max-w-[560px] leading-relaxed reveal-up delay-2">
              A modern Indian combat-sports organization developing structured competition, athletes and opportunities across MMA and Boxing.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal-up delay-3">
              <Link to="/events" className="btn-primary" data-testid="hero-explore-btn">Explore the Era <ArrowRight size={16} /></Link>
              <Link to="/register" className="btn-secondary" data-testid="hero-register-btn">Register</Link>
            </div>
          </div>
        </div>
        <div className="absolute left-5 lg:left-10 bottom-6 type-meta text-[#666]">MMA · Boxing · India</div>
      </section>

      {/* UPCOMING EVENT */}
      <Section testId="home-event" eyebrow="02 / Upcoming Event" title="Presented by Ironveil Era">
        {event ? (
          <div className="grid lg:grid-cols-2 gap-10 items-stretch">
            <div className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[520px] border border-[#2a2a2a] overflow-hidden">
              <SmartImage path={event.poster_path} fallback="ring" alt={event.title} className="w-full h-full object-cover scale-[1.18] origin-[90%_55%]" />
              <div className="absolute top-4 left-4 bg-[#C8102E] text-white type-meta px-3 py-1.5" data-testid="event-status-badge">{event.status}</div>
            </div>
            <div className="flex flex-col justify-center card-ive p-8 lg:p-12">
              <div className="type-label text-[#C8102E] mb-4">{event.event_number || "IVE 01"} · {event.sport}</div>
              <h3 className="font-display text-4xl sm:text-5xl leading-[1]" data-testid="event-title">{event.title}</h3>
              <div className="mt-6 space-y-3 text-sm">
                <div><span className="text-[#888] type-meta mr-3">Location</span>{event.location}</div>
                {event.venue && <div><span className="text-[#888] type-meta mr-3">Venue</span>{event.venue}</div>}
                <div><span className="text-[#888] type-meta mr-3">Date</span>{event.date || "To be announced"}</div>
              </div>
              <p className="mt-6 text-[#a0a0a0] leading-relaxed">{event.description}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={`/events/${event.slug}`} className="btn-primary" data-testid="event-explore-btn">Explore Event <ArrowRight size={16} /></Link>
                <Link to="/register" className="btn-secondary" data-testid="event-register-btn">Register</Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-ive p-12 text-center text-[#888]">No upcoming events yet. Check back soon.</div>
        )}
      </Section>

      <HomeReels />

      {/* ABOUT */}
      <Section testId="home-about" eyebrow="03 / About Ironveil Era" title="A modern combat-sports platform."
        right={<Link to="/about" className="btn-text" data-testid="home-about-cta">About Ironveil Era <ArrowRight size={14} /></Link>}>
        <div className="grid lg:grid-cols-3 gap-10">
          <p className="text-[#d0d0d0] text-lg leading-relaxed lg:col-span-2">
            Ironveil Era is being built as a modern combat-sports platform focused initially on <span className="text-white">MMA</span> and <span className="text-white">Boxing</span> — creating structured competition, athlete visibility, opportunity and a stronger combat-sports ecosystem across India.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {["Discipline","Respect","Performance","Opportunity","Community","Legacy"].map((v) => (
              <div key={v} className="card-ive p-3 sm:p-4 font-display text-xl sm:text-2xl min-w-0 break-words">{v}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* FEATURED FIGHTERS */}
      <Section testId="home-fighters" eyebrow="04 / Featured Fighters" title="The Athletes"
        right={<Link to="/records" className="btn-text" data-testid="home-view-records">View All Records <ArrowRight size={14} /></Link>}>
        {athletes.length ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {athletes.map((a) => <AthleteCard key={a.id} athlete={a} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[1,2,3,4].map((i) => (
              <div key={i} className="card-ive relative aspect-[3/4]" data-testid={`placeholder-athlete-${i}`}>
                <img src={i%2? IMG.fighter : IMG.athlete2} alt="Placeholder athlete" className="w-full h-full object-cover opacity-70" />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black to-transparent">
                  <div className="type-meta text-[#C8102E]">Placeholder</div>
                  <div className="type-card mt-1">Athlete 0{i}</div>
                  <div className="text-xs text-[#888] mt-1">MMA · Amateur</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* RANKINGS PREVIEW */}
      <Section testId="home-rankings" eyebrow="05 / Rankings & Records" title="MMA · Boxing · Amateur · Pro"
        right={<Link to="/records" className="btn-text" data-testid="home-explore-records">Explore Records <ArrowRight size={14} /></Link>}>
        <div className="card-ive divide-y divide-[#2a2a2a]">
          {(athletes.length ? athletes : [{}, {}, {}, {}]).slice(0, 5).map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm">
              <div className="col-span-1 type-rank text-[#C8102E]">{String(i+1).padStart(2,"0")}</div>
              <div className="col-span-5 sm:col-span-4 font-display text-xl sm:text-2xl">{a.name || `Placeholder Athlete ${i+1}`}</div>
              <div className="col-span-3 sm:col-span-2 text-[#a0a0a0] text-xs">{a.sport || "MMA"}</div>
              <div className="hidden sm:block col-span-2 text-[#a0a0a0] text-xs">{a.weight_class || "Lightweight"}</div>
              <div className="col-span-3 text-xs text-right sm:text-left">
                <span className="text-white font-semibold">{a.wins ?? 0}</span>
                <span className="text-[#666]"> · </span>
                <span className="text-[#a0a0a0]">{a.losses ?? 0}</span>
                <span className="text-[#666]"> · </span>
                <span className="text-[#a0a0a0]">{a.draws ?? 0}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* PARTNERS */}
      <Section testId="home-partners" eyebrow="06 / Partners" title="Building With Us"
        right={<Link to="/partners" className="btn-text" data-testid="home-become-partner">Become a Partner <ArrowRight size={14} /></Link>}>
        {partners.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.slice(0, 6).map((p) => (
              <Link to="/partners" key={p.id} className="card-ive p-5 flex gap-4 items-center" data-testid={`home-partner-${p.id}`}>
                {p.category !== "Academy" && (p.photo_path || p.logo_path) && <SmartImage path={p.photo_path || p.logo_path} fallback="fighter" alt={p.coach_name || p.name} className="w-16 h-16 object-cover border border-[#2a2a2a] shrink-0" />}
                <div className="min-w-0">
                  <div className="type-meta text-[#C8102E]">{p.category} Partner</div>
                  <div className="font-display text-2xl mt-1 leading-none truncate">{p.name}</div>
                  {p.category !== "Academy" && p.coach_name && <div className="text-sm font-semibold mt-1">Head Coach — {p.coach_name}</div>}
                  <div className="type-meta text-[#888] mt-1">{p.category === "Academy" ? p.location : [p.sport, p.location].filter(Boolean).join(" · ")}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="placeholder-partners">
            {["VENUM","EVERLAST","HAYABUSA","FAIRTEX","REEBOK","TITLE"].map((n) => (
              <div key={n} className="card-ive h-24 flex items-center justify-center">
                <span className="font-display text-2xl text-[#a0a0a0]">{n}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* FINAL CTA */}
      <section className="relative border-t border-[#151515] py-24 lg:py-32 bg-black overflow-hidden" data-testid="home-cta">
        <img src={IMG.gloves} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 text-center">
          <div className="type-label text-[#C8102E] mb-4">07 / Enter</div>
          <h2 className="type-h1">Enter the Era.</h2>
          <p className="mt-6 text-[#d0d0d0] max-w-xl mx-auto">Compete. Partner. Or become part of the Ironveil Era community.</p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/register" className="btn-primary" data-testid="cta-register">Register</Link>
            <Link to="/partners" className="btn-secondary" data-testid="cta-partner">Become a Partner</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function AthleteCard({ athlete }) {
  return (
    <Link to={`/athletes/${athlete.slug}`} className="card-ive relative aspect-[3/4] block group overflow-hidden" data-testid={`athlete-card-${athlete.id}`}>
      <SmartImage path={athlete.photo_path} fallback="fighter" alt={athlete.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
        <div className="type-meta text-[#C8102E]">{athlete.level} · {athlete.sport}</div>
        <div className="type-card mt-1">{athlete.name}</div>
        <div className="text-xs text-[#888] mt-1">{athlete.weight_class} · {athlete.wins}-{athlete.losses}-{athlete.draws}</div>
      </div>
    </Link>
  );
}
